import { Injectable, inject } from '@angular/core';
import {ApiService} from '../api/api.service';
import {Observable} from 'rxjs';
import {Subject} from 'rxjs';
import {IUser, User} from '../../models';
import {UserRoles} from '../../enums';
import {SessionStorage} from 'ngx-webstorage';
import {HttpResponse} from '@angular/common/http';
import {MsAuthService} from '../../module/ms-auth';

@Injectable()
export class UserService {
    private apiService = inject(ApiService);
    private authService = inject(MsAuthService);

    private userInfo: User = null;
    private userInfoSubject = new Subject<User>();

    private accessTokenSubject = new Subject<string>();

    @SessionStorage() private userHasLogin = false;
    @SessionStorage() private userIsAdmin = false;
    @SessionStorage() private userIsRiskManager = false;
    @SessionStorage() private userIsTenderManager = false;
    @SessionStorage() private userIsCompanyAdmin = false;

    public onUserInfoChange(): Observable<IUser> {
        return this.userInfoSubject.asObservable();
    }

    public getUserInfo(): Promise<User> {
        if (this.userInfo) {
            return Promise.resolve(this.userInfo);
        }
        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve, reject) => {
            await this.authService.isReady();
            if (!this.authService.isAuthenticated()) {
                this.authService.login();

            } else {
                this.apiService.get('/users/current/info').then(async (userInfoResponse: HttpResponse<IUser>) => {
                    this.userInfo = new User(userInfoResponse.body);
                    this.userInfoSubject.next(this.userInfo);
                    this.userHasLogin = true;
                    await this.setUserRoleFlags();
                    resolve(this.userInfo);
                }).catch((error) => {
                    this.userHasLogin = this.authService.isAuthenticated();
                    this.userIsAdmin = false;
                    this.userIsRiskManager = false;
                    this.userIsTenderManager = false;
                    this.userIsCompanyAdmin = false;
                    reject(error);
                });
            }
        });
    }

    public async userHasAnyRole(roles: string[]): Promise<boolean> {
        if (!this.userInfo) {
            await this.getUserInfo();
        }

        let hasRole = false;
        if (this.userHasLogin && this.userInfo) {
            for (const role of roles) {
                if (!hasRole) {
                    const roleIndex = this.userInfo.roleNames.indexOf(role);
                    hasRole = roleIndex >= 0;
                }
            }
        }

        return hasRole;
    }

    public async userHasNoRoles(): Promise<boolean> {
        if (!this.userInfo) {
            await this.getUserInfo();
        }

        let noRoles = false;
        if (this.userHasLogin && this.userInfo) {
            noRoles = this.userInfo.roleNames.length === 0;
        }

        return noRoles;
    }

    public userHasProperOwnerRoles(userInfo: IUser): boolean {
        const ownerRoles = ['ROLE_TENDER_MANAGER', 'ROLE_COMPANY_PROJECT_EDITOR', 'ROLE_PROJECT_EDITOR', 'ROLE_COMPANY_ADMIN'];

        return ownerRoles.filter(value => userInfo.roleNames.includes(value)).length > 0;
    }

    private async setUserRoleFlags() {
        this.userIsRiskManager = await this.userHasAnyRole([UserRoles.RISK_MANAGER]);
        this.userIsTenderManager = await this.userHasAnyRole([UserRoles.TENDER_MANAGER]);
        this.userIsAdmin = await this.userHasAnyRole([UserRoles.ADMIN]);
        this.userIsCompanyAdmin = await this.userHasAnyRole([UserRoles.COMPANY_ADMIN]);
    }

    public resetUserInfo(): Promise<User> {
        this.userInfo = null;
        return this.getUserInfo();
    }
}
